import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { APP_API_URL } from '../../utils';
import Swal from 'sweetalert2';
import DuoModal from '../../components/ui/duo-modal';
import UIDropdown from '../../components/ui/ui-dropdown';

export default function Add({ open, onClose, refreshData }) {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    variant_id: '',
    quantity: 1,
    reason: 'Casser',
  });
  const [selectedProduct, setSelectedProduct] = useState({ label: 'Sélectionner un produit', value: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchVariants = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${APP_API_URL}/variants`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        setProducts(response.data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des variantes :', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger les produits.',
        });
      }
    };
    fetchVariants();
  }, [open]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleProductSelect = (option) => {
    setSelectedProduct(option);
    setFormData((prev) => ({ ...prev, variant_id: option.value }));
    setErrors((prev) => ({ ...prev, variant_id: '' }));
  };

  const resetForm = () => {
    setFormData({ variant_id: '', quantity: 1, reason: 'Casser' });
    setSelectedProduct({ label: 'Sélectionner un produit', value: '' });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.variant_id) newErrors.variant_id = 'Le produit est requis.';
    if (!formData.quantity || Number(formData.quantity) <= 0) {
      newErrors.quantity = 'La quantité doit être un nombre positif.';
    }
    if (!formData.reason?.trim()) newErrors.reason = 'Le motif est requis.';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/stock-adjustments`,
        {
          variant_id: parseInt(formData.variant_id, 10),
          quantity: parseInt(formData.quantity, 10),
          reason: formData.reason.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 201) {
        await Swal.fire({
          icon: 'success',
          title: 'Succès',
          text: response.data.message || "L'ajustement a été enregistré.",
          timer: 2000,
          showConfirmButton: false,
        });
        resetForm();
        onClose();
        refreshData();
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response.data.errors);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: error.response?.data?.message || "Une erreur est survenue.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DuoModal
      title="Ajouter un ajustement de stock"
      onClose={handleClose}
      footer={
        <>
          <button type="button" className="duo-btn duo-btn--ghost" onClick={handleClose} disabled={isLoading}>
            Annuler
          </button>
          <button type="submit" form="add-stock-adjustment-form" className="duo-btn duo-btn--primary" disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id="add-stock-adjustment-form" onSubmit={handleSubmit}>
        <div className="duo-form-group">
          <label className="duo-form-label">Produit / variante *</label>
          <UIDropdown
            options={products.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            selectedOption={selectedProduct}
            onSelect={handleProductSelect}
            enableSearch
          />
          {errors.variant_id && <p className="text-danger small mt-1 mb-0">{errors.variant_id}</p>}
        </div>
        <div className="duo-form-group">
          <label htmlFor="adj-quantity" className="duo-form-label">
            Quantité *
          </label>
          <input
            type="number"
            className="duo-input"
            id="adj-quantity"
            name="quantity"
            min="1"
            value={formData.quantity}
            onChange={handleChange}
          />
          {errors.quantity && <p className="text-danger small mt-1 mb-0">{errors.quantity}</p>}
        </div>
        <div className="duo-form-group mb-0">
          <label htmlFor="adj-reason" className="duo-form-label">
            Motif *
          </label>
          <textarea
            className="duo-input"
            id="adj-reason"
            name="reason"
            rows={4}
            value={formData.reason}
            onChange={handleChange}
            placeholder="Ex. Casse, inventaire, correction..."
          />
          {errors.reason && <p className="text-danger small mt-1 mb-0">{errors.reason}</p>}
        </div>
      </form>
    </DuoModal>
  );
}
